import { addonIds } from "../../../../util/constants";
import {
  ADDONS_MODULE_QUERY_KEYS,
  DEFAULT_RAIL_SECTION,
  ORGANISATION_MODULE_ID,
} from "./constants";

const MISSING_ROW_SIGNATURE = "__missing__";
const UNLIMITED_USERS_LIMIT = 9999;

const toNumber = (value) => Number(value || 0);

export const getSelectedModuleFromSearch = (search = "") => {
  const searchParams = new URLSearchParams(search);

  for (const key of ADDONS_MODULE_QUERY_KEYS) {
    const value = searchParams.get(key);
    if (value) return value;
  }

  return null;
};

const toTagModuleName = (moduleName) => {
  const normalized = String(moduleName || "Module").trim();
  return normalized ? normalized.toUpperCase() : "MODULE";
};

export const getModuleTagLabel = ({
  isOfferFlow,
  moduleName,
  isExpiredTrial,
}) => {
  const tagModuleName = toTagModuleName(moduleName);
  if (isExpiredTrial) return `${tagModuleName} EXPIRED TRIAL`;

  return isOfferFlow
    ? `${tagModuleName} PLAN OFFER`
    : `CURRENT ${tagModuleName} PLAN`;
};

export const isScheduledForRemoval = (row) =>
  Boolean(row?.usage?.scheduled) &&
  toNumber(row?.usage?.scheduled?.overages) === 0 &&
  toNumber(row?.usage?.scheduled?.overagesChange) < 0;

export const normalizePendingAddonId = (addonId) =>
  addonId === addonIds.USER || addonId === addonIds.ADDITIONAL_USERS
    ? addonIds.ADDITIONAL_USERS
    : addonId;

export const findAddonRowById = (rows, addonId) =>
  rows.find((item) => item?.addonId === addonId);

export const buildAddonRowSignature = (row) => {
  if (!row) return MISSING_ROW_SIGNATURE;

  const rowAmountMinor = toNumber(row?.rowAmountMinor);
  const purchasedQuantity = toNumber(
    row?.purchasedQuantity ?? row?.purchasedAllowance,
  );
  const usageOverages = toNumber(row?.usage?.overages);
  const usageProratedOverages = toNumber(row?.usage?.proratedOverages);
  const usageTotalUsed = toNumber(row?.usage?.totalUsed);
  const scheduledOverages = toNumber(row?.usage?.scheduled?.overages);
  const scheduledOveragesChange = toNumber(
    row?.usage?.scheduled?.overagesChange,
  );

  return [
    Boolean(row?.isActive),
    rowAmountMinor,
    purchasedQuantity,
    usageOverages,
    usageProratedOverages,
    usageTotalUsed,
    scheduledOverages,
    scheduledOveragesChange,
  ].join("|");
};

export const getPurchasedAddonUnits = (item) => {
  const overages = toNumber(item?.usage?.overages);
  const proratedOverages = toNumber(
    item?.usage?.proratedOverages ?? item?.usage?.proratedoverages,
  );
  const quantity = toNumber(item?.quantity ?? item?.addon?.quantity);

  return overages + proratedOverages + quantity;
};

export const isConfiguredOrganisationAddon = (item) => {
  if (!item) return false;

  const rowAmountMinor = toNumber(item?.rowAmountMinor);
  return rowAmountMinor > 0 || getPurchasedAddonUnits(item) > 0;
};

export const isOrganisationModuleSelection = (moduleId) =>
  moduleId === ORGANISATION_MODULE_ID;

export const getDefaultRailSection = () => DEFAULT_RAIL_SECTION;

export const getIncludedUsersByModuleBreakdown = (includedUsersByPlan = []) => {
  const includedByModule = Object.entries(
    includedUsersByPlan.reduce((accumulator, entry) => {
      const moduleName = String(entry.moduleName || "Plan").trim();
      const current = accumulator[moduleName] || 0;
      const value = toNumber(entry.includedUsers);

      if (current >= UNLIMITED_USERS_LIMIT || value >= UNLIMITED_USERS_LIMIT) {
        accumulator[moduleName] = UNLIMITED_USERS_LIMIT;
        return accumulator;
      }

      accumulator[moduleName] = current + value;
      return accumulator;
    }, {}),
  );

  const displayOrder = {
    "Organisation add-ons": 0,
    Receivables: 1,
    "Cash flow": 2,
  };

  const orderedModuleEntries = includedByModule.sort(([firstName], [secondName]) => {
    const firstOrder = displayOrder[String(firstName)] ?? 99;
    const secondOrder = displayOrder[String(secondName)] ?? 99;

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }

    return String(firstName).localeCompare(String(secondName));
  });

  const hasUnlimitedTotal = includedByModule.some(
    ([, includedUsers]) => toNumber(includedUsers) >= UNLIMITED_USERS_LIMIT,
  );

  const includedUsersTotal = hasUnlimitedTotal
    ? UNLIMITED_USERS_LIMIT
    : includedByModule.reduce(
        (total, [, includedUsers]) => total + toNumber(includedUsers),
        0,
      );

  return {
    orderedModuleEntries,
    includedUsersTotal,
    unlimitedLimit: UNLIMITED_USERS_LIMIT,
  };
};
